import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Compass, Lock, Loader2, Shovel, Tractor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "excavator", label: "Excavator", icon: Shovel, desc: "Depth, slope and model guidance for digging machines." },
  { id: "dozer", label: "Dozer", icon: Tractor, desc: "Blade guidance for shaping, spreading and slopes." },
  { id: "survey", label: "Survey", icon: Compass, desc: "Rover and base kits for control and as-builts." },
];

const VARIANTS = {
  excavator: [
    { id: "exc-25d", tag: "A", name: "2.5D — EasyNAV EMG100", desc: "Depth & slope from a field reference", custom: ["Tilt", "Tilt rotator", "Swing boom", "Knuckle boom"] },
    { id: "exc-3d", tag: "B", name: "3D — MCNAV TX73", desc: "Model-driven, live CAD in the cab", custom: ["Tilt", "Tilt rotator", "Swing boom", "Knuckle boom"] },
  ],
  dozer: [
    { id: "doz-auto", tag: "A", name: "Full auto — TD63 Pro", desc: "Automatic blade control — hands-off grade", custom: [] },
    { id: "doz-indicate", tag: "B", name: "Indicate only — blade guidance", desc: "Guidance display only — the operator drives the blade", custom: [] },
  ],
  survey: [
    { id: "srv-rover", tag: "A", name: "GNSS rover kit", desc: "Millimetre RTK anywhere on site", custom: [] },
    { id: "srv-base", tag: "B", name: "GNSS base kit", desc: "Fixed reference for your whole crew", custom: [] },
  ],
};

export default function QuoteBuilder() {
  const [type, setType] = useState(TYPES[0]);
  const [variant, setVariant] = useState(VARIANTS.excavator[0]);
  const [units, setUnits] = useState(2);
  const [customs, setCustoms] = useState([]);
  const [machineType, setMachineType] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const pickType = (t) => {
    setType(t);
    setVariant(VARIANTS[t.id][0]);
    setCustoms([]);
  };

  const pickVariant = (v) => {
    setVariant(v);
    setCustoms([]);
  };

  const toggleCustom = (c) =>
    setCustoms(customs.includes(c) ? customs.filter((x) => x !== c) : [...customs, c]);

  const validPhone = /^[+\d][\d\s().-]{5,}$/.test(phone.trim());

  const submit = async (e) => {
    e.preventDefault();
    const errs = [];
    if (!name.trim()) errs.push("name");
    if (!validPhone) errs.push("phone number");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errs.push("email");
    if (errs.length) {
      toast.error(`Add your ${errs.join(", ")} to see your indicative price.`);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        name,
        email,
        phone,
        company: machineType,
        region: "",
        fleet_size: `${units} unit${units > 1 ? "s" : ""}`,
        interest: `Quote — ${type.label} / ${variant.name}`,
        quote_variant: variant.id,
        quote_units: units,
        quote_custom: customs.join(", "),
        message: `QUOTE REQUEST: ${type.label} — ${units}x ${variant.name} | Machine: ${
          machineType.trim() || "not specified"
        } | Customization: ${customs.length ? customs.join(", ") : "Standard config"}`,
      });
      setRevealed(true);
      toast.success("Request received — your indicative price is on its way.");
    } catch (err) {
      toast.error("Submission failed — check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-line bg-ink px-4 py-3 text-base text-white placeholder:text-fog/50 outline-none transition-colors focus:border-signal";

  return (
    <div data-testid="quote-builder" className="border border-line bg-surface">
      <div className="p-8 sm:p-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">1 · What are you equipping?</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {TYPES.map((t) => (
            <button
              key={t.id}
              data-testid={`quote-type-${t.id}`}
              onClick={() => pickType(t)}
              className={`border p-5 text-left transition-colors ${
                type.id === t.id ? "border-signal bg-signal/10" : "border-line hover:border-fog"
              }`}
            >
              <span className="flex items-center justify-between">
                <t.icon className={`h-6 w-6 ${type.id === t.id ? "text-neon" : "text-fog"}`} />
                <span
                  className={`flex h-5 w-5 items-center justify-center border ${
                    type.id === t.id ? "border-signal bg-signal text-deep" : "border-line"
                  }`}
                >
                  {type.id === t.id && <Check className="h-3.5 w-3.5" />}
                </span>
              </span>
              <span className="mt-3 block font-display text-xl font-bold uppercase text-white">{t.label}</span>
              <span className="mt-1 block text-xs leading-relaxed text-fog">{t.desc}</span>
            </button>
          ))}
        </div>

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-fog">2 · Pick your system</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {VARIANTS[type.id].map((v) => (
            <button
              key={v.id}
              data-testid={`quote-variant-${v.id}`}
              onClick={() => pickVariant(v)}
              className={`border p-5 text-left transition-colors ${
                variant.id === v.id ? "border-signal bg-signal/10" : "border-line hover:border-fog"
              }`}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span
                    className={`flex h-6 w-6 items-center justify-center font-mono text-[11px] ${
                      variant.id === v.id ? "bg-signal text-deep" : "border border-line text-fog"
                    }`}
                  >
                    {v.tag}
                  </span>
                  <span className="font-display text-lg font-bold uppercase text-white">{v.name}</span>
                </span>
                <span
                  className={`flex h-5 w-5 items-center justify-center border ${
                    variant.id === v.id ? "border-signal bg-signal text-deep" : "border-line"
                  }`}
                >
                  {variant.id === v.id && <Check className="h-3.5 w-3.5" />}
                </span>
              </span>
              <span className="mt-3 block text-xs text-fog">{v.desc}</span>
            </button>
          ))}
        </div>

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-fog">3 · How many units?</p>
        <div className="mt-4 flex items-center">
          <input
            type="range"
            min="1"
            max="20"
            value={units}
            onChange={(e) => setUnits(Number(e.target.value))}
            data-testid="quote-units-slider"
            aria-label="Number of units"
            className="w-full accent-[#00FF66]"
          />
          <span className="ml-6 shrink-0 font-display text-3xl font-black text-neon" data-testid="quote-unit-count">
            {units}
          </span>
        </div>

        {type.id !== "survey" && (
          <div className="mt-10" data-testid="quote-machine-type-field">
            <label className="block">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">
                Machine type <span className="text-fog/50">(optional)</span>
              </span>
              <input
                value={machineType}
                onChange={(e) => setMachineType(e.target.value)}
                data-testid="quote-machine-type"
                placeholder="e.g. CAT 320, Komatsu PC210, D65…"
                className={`${inputCls} mt-3`}
              />
            </label>
          </div>
        )}

        {variant.custom.length > 0 && (
          <div className="mt-10" data-testid="quote-customize-step">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">4 · Customize your machine</p>
            <p className="mt-2 text-xs text-fog">Select everything that applies — we configure the system to match.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {variant.custom.map((c, i) => (
                <label
                  key={c}
                  className={`flex cursor-pointer items-center gap-3 border p-4 transition-colors ${
                    customs.includes(c) ? "border-signal bg-signal/5" : "border-line hover:border-fog"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={customs.includes(c)}
                    onChange={() => toggleCustom(c)}
                    data-testid={`quote-custom-${i}`}
                    className="h-4 w-4 accent-[#10B981]"
                  />
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-line font-mono text-[11px] text-fog">
                    {String.fromCharCode(97 + i)}
                  </span>
                  <span className="text-sm text-white">{c}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-line bg-deep p-8 sm:p-12" data-testid="quote-build-summary">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">Your build</p>
        <div className="mt-6 grid gap-2 font-mono text-xs text-fog sm:grid-cols-2 lg:grid-cols-4" data-testid="quote-breakdown">
          <p>
            <span className="text-fog/60">Type — </span>
            <span className="text-white">{type.label}</span>
          </p>
          <p>
            <span className="text-fog/60">System — </span>
            <span className="text-white">{variant.name}</span>
          </p>
          <p>
            <span className="text-fog/60">Units — </span>
            <span className="text-white">{units}</span>
          </p>
          <p data-testid="quote-custom-summary">
            <span className="text-fog/60">Customization — </span>
            <span className="text-white">{customs.length ? customs.join(", ") : "Standard config"}</span>
          </p>
        </div>

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-fog">Your indicative price</p>
        {!revealed ? (
          <div data-testid="quote-price-locked" className="mt-3 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-6 sm:flex-row sm:items-center">
            <div>
              <p className="font-display text-5xl font-black text-fog/50 blur-[6px] select-none">$•••,•••</p>
              <p className="mt-3 max-w-md text-xs leading-relaxed text-fog">
                Add your details and submit to unlock your indicative price — no commitment, no payment.
              </p>
            </div>
            <Lock className="h-8 w-8 shrink-0 text-signal" />
          </div>
        ) : (
          <div data-testid="quote-price-revealed" className="mt-3 border border-signal/50 bg-signal/5 p-6">
            <p className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-neon">
              <Check className="h-6 w-6" /> Request received
            </p>
            <p className="mt-3 text-xs leading-relaxed text-fog">
              Your indicative price for the {variant.name} is being prepared — it will reach {email} and a specialist
              will call {phone} within one business day to confirm final pricing.
            </p>
          </div>
        )}

        {!revealed && (
          <div className="mt-10" data-testid="quote-details-step">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">5 · Your details</p>
            <p className="mt-2 text-xs text-fog">
              Where we send your indicative price — no spam, no commitment, no payment.
            </p>
          </div>
        )}
        {!revealed && (
          <form onSubmit={submit} className="mt-6" data-testid="quote-form" noValidate>
            <div className="grid gap-3 sm:grid-cols-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="quote-name"
                placeholder="Your name *"
                aria-label="Your name"
                className={inputCls}
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="quote-phone"
                type="tel"
                placeholder="Phone number *"
                aria-label="Phone number"
                className={inputCls}
              />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="quote-email"
                type="email"
                placeholder="Email *"
                aria-label="Email"
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-btn"
              className="group mt-4 inline-flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep disabled:cursor-not-allowed disabled:opacity-50 sm:w-fit"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
              {sending ? "Sending…" : "See my indicative price"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
